import { useRef, useState, useCallback, TouchEvent } from 'react'

interface PullToRefreshConfig {
  onRefresh: () => Promise<void>
  threshold?: number
  maxPullDistance?: number
  resistance?: number
}

export const usePullToRefresh = (config: PullToRefreshConfig) => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const touchStartRef = useRef<number | null>(null)
  const scrollTopRef = useRef<number>(0)

  const threshold = config.threshold ?? 80
  const maxPull = config.maxPullDistance ?? 120
  const resistance = config.resistance ?? 2.5

  const handleTouchStart = useCallback((e: TouchEvent<HTMLElement>) => {
    scrollTopRef.current = window.scrollY || document.documentElement.scrollTop

    if (scrollTopRef.current === 0) {
      touchStartRef.current = e.touches[0].clientY
    }
  }, [])

  const handleTouchMove = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      if (touchStartRef.current === null || isRefreshing) return

      const currentY = e.touches[0].clientY
      const delta = currentY - touchStartRef.current

      if (delta > 0) {
        const distance = Math.min(delta / resistance, maxPull)
        setPullDistance(distance)

        if (distance > 10) {
          e.preventDefault()
        }
      }
    },
    [isRefreshing, maxPull, resistance]
  )

  const handleTouchEnd = useCallback(async () => {
    if (pullDistance >= threshold) {
      setIsRefreshing(true)

      try {
        await config.onRefresh()
      } catch (error) {
        console.error('[PTR] Refresh failed:', error)
        throw error
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }

    touchStartRef.current = null
  }, [pullDistance, threshold, config])

  const progress = Math.min((pullDistance / threshold) * 100, 100)

  return {
    isRefreshing,
    pullDistance,
    progress,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  }
}
